import { computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Component, effect, OnDestroy, OnInit, input } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { PwaService } from 'src/app/services/pwa.service';
import {
  IonButtons,
  IonHeader,
  IonTitle,
  IonToolbar,
  Platform,
  IonContent,
  IonBackButton,
} from '@ionic/angular/standalone';
import { PushService } from 'src/app/services/push.service';
import { TranslateModule } from '@ngx-translate/core';
import { PermissionType } from 'src/app/enums/permission-type';
import { SettingsControlComponent } from 'src/app/components/settings-control/settings-control.component';
import { PermissionService } from 'src/app/services/permission.service';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/services/user.service';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  standalone: true,
  selector: 'app-notifications',
  templateUrl: './notifications.page.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    IonContent,
    IonBackButton,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonTitle,
    SettingsControlComponent,
    NgTemplateOutlet,
  ],
})
export class NotificationsPage implements OnInit, OnDestroy {
  embedded = input(false);
  isPwa: boolean;
  permissionsForm: FormGroup;
  private resumeSubscription: Subscription;
  private isBackground: boolean = false;
  readonly user = this.userService.user;
  readonly PermissionType = PermissionType;

  private readonly translate = inject(TranslateService);

  readonly emailDescription = computed(() => {
    const user = this.user();
    const base = this.translate.instant('settings.control.email.desc');
    if (user && user.email) {
      const suffix = this.translate.instant(
        'settings.control.email.suffix.with',
        { email: user.email }
      );
      return `${base} ${suffix}`;
    } else {
      const suffix = this.translate.instant(
        'settings.control.email.suffix.without'
      );
      return `${base} ${suffix}`;
    }
  });

  constructor(
    private pushService: PushService,
    private fb: FormBuilder,
    private platform: Platform,
    private pwaService: PwaService,
    private permissionService: PermissionService,
    private userService: UserService
  ) {
    this.createPermissionsForm();

    effect(() => {
      if (this.user()) {
        this.checkStatuses();
      }
    });
  }

  ngOnInit() {
    this.isPwa = this.pwaService.isPwa();

    this.resumeSubscription = this.platform.resume.subscribe(() => {
      if (this.isBackground) {
        this.checkStatuses(true);
        this.isBackground = false;
      }
    });
  }

  ionViewWillEnter() {
    this.checkStatuses();
  }

  ngOnDestroy() {
    this.resumeSubscription.unsubscribe();
  }

  createPermissionsForm() {
    this.permissionsForm = this.fb.group({
      pushEnabled: this.fb.control(false),
      emailEnabled: this.fb.control(false),
    });

    this.permissionsForm.get('pushEnabled').valueChanges.subscribe((value) => {
      this.handleToggle(value, PermissionType.PUSH);
    });

    this.permissionsForm.get('emailEnabled').valueChanges.subscribe((value) => {
      this.handleToggle(value, PermissionType.EMAIL);
    });
  }

  get pushControl(): FormControl<boolean> {
    return this.permissionsForm.controls.pushEnabled as FormControl<boolean>;
  }

  get emailControl(): FormControl<boolean> {
    return this.permissionsForm.controls.emailEnabled as FormControl<boolean>;
  }

  async handleToggle(value: boolean, type: PermissionType) {
    if (!value) {
      if (type === PermissionType.EMAIL) {
        this.permissionService.removePreference(type);
        return;
      }
    } else {
      const granted = await this.permissionService.requestPermission(type);
      if (granted) {
        this.permissionService.addPreference(type);
        if (type === PermissionType.PUSH) {
          this.pushService.enablePush();
        }
        return;
      }
    }
    const confirm =
      await this.permissionService.getOpenSystemSettingsPermission(type);
    if (confirm) {
      const action = this.permissionService.getSettingsAction(type);
      await action();
      this.isBackground = true;
    } else {
      this.permissionsForm
        .get(`${type}Enabled`)
        ?.setValue(!value, { emitEvent: false });
    }
  }

  private async checkStatuses(updatePush?: boolean) {
    const [pushEnabled, emailEnabled] = await Promise.all([
      this.permissionService.isPermissionEnabled(PermissionType.PUSH),
      this.permissionService.isPermissionEnabled(PermissionType.EMAIL),
    ]);

    this.permissionsForm
      .get('pushEnabled')
      ?.setValue(pushEnabled, { emitEvent: false });
    this.permissionsForm
      .get('emailEnabled')
      ?.setValue(emailEnabled, { emitEvent: false });

    if (!updatePush) {
      return;
    }

    if (pushEnabled) {
      this.pushService.enablePush();
    } else {
      this.pushService.disablePush();
    }
  }
}
